import styled from "styled-components";
import store from "../../../_services/store";
import labels from '../../../_translations/molecules.json';
import { COLORS } from "../../../constants";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../router/routes";
import { CardTitle, Letter, SeparatorLine } from "../../_ui";

export const MoleculeSectionContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  align-self: stretch;
  flex-wrap: wrap;
  gap: 10px;
`;

export const CategorySelectorContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: -20px;
  margin-bottom: 16px;

  /* Desktop : tout sur une seule ligne */
  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;

export const CategoryPill = styled.button<{ $active: boolean }>`
  /* Mobile : 2 pastilles par ligne (2x2 avec 4 catégories) */
  flex: 1 1 40%;
  min-width: 0;
  border: none;
  cursor: pointer;
  padding: 10px 18px;
  border-radius: 22px;
  font-family: Poppins;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: background 0.2s ease, color 0.2s ease;
  background: ${props => (props.$active ? COLORS.PRIMARY : COLORS.WHITE)};
  color: ${props => (props.$active ? COLORS.WHITE : COLORS.SECONDARY)};

  &:hover {
    background: ${props => (props.$active ? COLORS.SECONDARY : '#F7EFEE')};
  }

  /* Desktop : colonnes égales sur une seule ligne */
  @media (min-width: 768px) {
    flex: 1 1 0;
    padding: 10px 12px;
  }
`;

type TCategoryItem = { key: string; label: string; active: boolean; onClick: () => void };

export const CategorySelector = ({ items }: { items: TCategoryItem[] }) => (
  <CategorySelectorContainer>
    {items.map(item => (
      <CategoryPill
        key={item.key}
        type="button"
        $active={item.active}
        onClick={item.onClick}
      >
        {item.label}
      </CategoryPill>
    ))}
  </CategorySelectorContainer>
);

export const MoleculeCardContainer = styled.div<{ $isClickable: boolean }>`
  display: flex;
  height: 48px;
  padding: 10px 20px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  border-radius: 20px;
  background: ${COLORS.WHITE};
  cursor: ${props => props.$isClickable ? 'pointer' : 'default'};
  opacity: ${props => props.$isClickable ? 1 : 0.6};
  box-sizing:border-box;
  &:hover {
    box-shadow: ${props => props.$isClickable ? 'rgba(0, 0, 0, 0.5) 0px 0px 6px' : 'none'};
  }
`;

export const MoleculeCardType = styled.p`
  color: ${COLORS.DARK};
  font-family: Poppins;
  font-size: 12px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  margin: 0px;
`;

export const MoleculeSection = ({ moleculeSection }) => {
  const letter = Object.keys(moleculeSection)[0];
  return (
    <div>
      <Letter>{letter}</Letter>
      <SeparatorLine />
      <MoleculeSectionContainer>
        {moleculeSection[letter].map((e: TMolecule, i: number) => <MoleculeCard key={`SectionCard_${i}`} molecule={e} />)}
      </MoleculeSectionContainer>
    </div>
  )
}

// Correspondance valeur `type` BDD -> libellé traduit
const MOLECULE_TYPE_LABEL_KEY = {
  "1": "btn1",
  "2": "btn2",
  "3": "btn3",
  "4": "btn4",
} as const;

const getMoleculeTypeLabel = (type: string) => {
  const barbtn = labels[store.currentLanguage].barbtn;
  const key = MOLECULE_TYPE_LABEL_KEY[type as keyof typeof MOLECULE_TYPE_LABEL_KEY];
  return key ? barbtn[key] : barbtn.btn2;
};

export const MoleculeCard = ({ molecule }: { molecule: TMolecule }) => {
  const navigate = useNavigate();
  const isClickable = Boolean(molecule.id && molecule.id.trim() !== "");
  
  const openMoleculeDetail = () => {
    if (isClickable) {
      navigate(ROUTES.MOLECULE_DETAIL(molecule.id));
    }
  }

  return (
    <MoleculeCardContainer 
      className="fadeIn" 
      onClick={openMoleculeDetail}
      $isClickable={isClickable}
    >
      <CardTitle>{molecule.title}</CardTitle>
      <MoleculeCardType>
        {getMoleculeTypeLabel(molecule.type)}
      </MoleculeCardType>
    </MoleculeCardContainer>
  )
}
